import * as React from 'react';
import { connect } from 'react-redux';
import injectSheet from 'react-jss';
import { Button, Checkbox, Input, Select } from 'antd';
import { getPorts } from 'src/api/containerTracking';
import { withRouter } from 'react-router';
import SearchTagsInput from 'src/components/common/SearchTagsInput';
import { bindActionCreators } from 'redux';
import { bookingSetFilter, bookingApplyFilter } from 'src/actions/bookingInquiryActions';
import { getFFDimensions, getShipperDimensions } from 'src/selectors/importsFilter';
import { fetchAllTeams } from 'src/api/teams';
import { fetchAddresses } from 'src/api/organisationProfiling';
import { searchCountry } from 'src/api/country';
import { getPolPodTypesInquiryFilter } from '../InquiryUtils';
import { tokens } from 'src/design-system';

const defaultBookingFilterAccessTypeOptions = [
  { key: 'PUBLIC', label: 'Workplace' },
  { key: 'PRIVATE', label: 'Only Me' },
];

const styles = (theme) => ({
  mainDiv: {
    background: 'white',
    position: 'absolute',
    zIndex: 10,
    marginTop: '-11px',
    width: '100%',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #D9D9D9',
    boxShadow: '2px 2px 2px 0 rgba(0, 0, 0, 0.08)',
    '& .ant-calendar-picker-input': {
      padding: 0,
    },
  },
  childDiv: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: 16,
    '& .ant-checkbox-group-item': {
      minWidth: 80,
    },
  },
  leftElement: {
    width: '170px',
    marginRight: '16px',
  },
  selectStyle: {
    minWidth: '200px',
  },
  ffType: {
    backgroundColor: '#FFE7D5',
    border: '1px solid #FFC59A',
    padding: '0px 4px',
    marginLeft: '5px',
    fontSize: `${tokens.fontSize.caption}px`,
    borderRadius: `${tokens.radius.sm}px`,
  },
  groupType: {
    backgroundColor: '#EDE3FC',
    border: '1px solid #A082CE',
    padding: '0px 4px',
    marginLeft: '5px',
    fontSize: `${tokens.fontSize.caption}px`,
    borderRadius: `${tokens.radius.sm}px`,
  },
});
class BookingFilterPopup extends React.PureComponent<any, any> {
  state = {
    isSearching: true,
    sourcePortOptions: [],
    createData: {
      originPortIds: [],
      originPortNames: [],
    },
    accessTypeDropdownOptions: [],
    uniqueOriginCities: [] as {key: string, label: string}[],
    uniqueDestinationCities: [] as {key: string, label: string}[],
    podDropdown: [],
    polDropdown: [],
  };

  componentDidMount(): void {
    this.fetchConsignerAddress();
  }

  fetchConsignerAddress = async (queryString?: string) => {
    const response = await fetchAddresses(queryString ? { queryString, searchForCity: true } : { limit: 50 });
    if (response.isSuccess) {
      const addressesList = response.data || [];
      const originCities = {};
      const destinationCities = {};
      addressesList.forEach((address) => {
        if (address.city) {
          if (address.addressTypes?.includes('STUFFING')) {
            originCities[address.city] = {key: address.city, label: `${address.city} [${address.countryCode || '-'}]`};
          } 
          if (address.addressTypes?.includes('DESTUFFING')) {
            destinationCities[address.city] = {key: address.city, label: `${address.city} [${address.countryCode || '-'}]`};
          }
        }
      });
      this.setState({
        uniqueOriginCities : Object.values(originCities),
        uniqueDestinationCities: Object.values(destinationCities)
      });
    }
  }

  handleSearch = (type: string, query: string, portType: string) => {
    if (type === 'port') {
      this.searchForSourcePort(query, portType);
    } else if (type === 'city') {
      this.searchConsignerAddress(query, portType);
    } else {
      this.searchForSourceCountry(query, portType);
    }
  };

  searchConsignerAddress = async (queryString: string, stateKey: string) => {
    if (stateKey === 'polDropdown') {
      if (queryString) {
        await this.fetchConsignerAddress(queryString);
        this.setState({
          [stateKey]: this.state.uniqueOriginCities.filter(city => 
          city?.label.toLowerCase().includes(queryString?.toLowerCase())),
        });
      } else {
        await this.fetchConsignerAddress();
        this.setState({
          [stateKey]: this.state.uniqueOriginCities,
        });
      }
    } else if (stateKey === 'podDropdown') {
      if (queryString) {
        await this.fetchConsignerAddress(queryString);
        this.setState({
          [stateKey]: this.state.uniqueDestinationCities.filter(city => 
          city?.label.toLowerCase().includes(queryString?.toLowerCase())),
        });
      } else {
        await this.fetchConsignerAddress();
        this.setState({
          [stateKey]: this.state.uniqueDestinationCities,
        });
      }
    }
  }

  searchForSourcePort = async (queryString: string, portType: string) => {
    const response = await getPorts({
      queryString,
      type: ['DRY', 'WET'],
    });
    if (response.isSuccess) {
      this.setState({
        [portType]: response.data.map((el) => ({
          key: el.code,
          label: el.name.concat('[', `${el.code}`, ']'),
        })),
      });
    }
  };

  searchForSourceCountry = async (queryString: string, portType: string) => {
    const response = await searchCountry({ queryString });
    if (response.isSuccess) {
      this.setState({
        [portType]: response.data.map((country) => ({
          key: country.countryCode,
          label: country.countryName,
        })),
      });
    } else {
      this.setState({
        [portType]: [],
      });
    }
  };

  searchInquiryAccess = async () => {
    const response = await fetchAllTeams({ adminTeamRestriction: true });
    if (response.isSuccess) {
      const data = response.data.map((item) => ({
        key: item.id,
        label: `${item.name} (${item.reference_number})`,
      }));
      const updatedBookingFilterAccessTypeOptions = defaultBookingFilterAccessTypeOptions.concat(data);
      this.setState({
        accessTypeDropdownOptions: updatedBookingFilterAccessTypeOptions,
      });
      return;
    }
    this.setState({
      accessTypeDropdownOptions: defaultBookingFilterAccessTypeOptions,
    });
  };

  render() {
    const { classes, type, ffList, ffGroups, shipperList, filters, bookingSetFilter } = this.props;
    /*    const modes = [
      { label: 'FCL', value: 'FCL' },
      { label: 'LCL', value: 'LCL' },
      { label: 'Air Cargo', value: 'AIRCARGO' },
    ];
*/
    const {
      polDropdown,
      podDropdown
    } = this.state;
    const {
      polList,
      podList,
      originCityList,
      destinationCityList,
      polSearch,
      podSearch,
      originCountryList,
      destinationCountryList
    } = filters;

    let polValue = polList;
    let podValue = podList;

    if (polSearch === 'city') { 
      polValue = originCityList
    } else if (polSearch === 'country') {
      polValue = originCountryList
    }

    if (podSearch === 'city') { 
      podValue = destinationCityList;
    }  else if (podSearch === 'country') {
      podValue = destinationCountryList
    }
    const FFsAndGroupsList = [...ffList.map((ff) => ({...ff, isGroup: false})), ...ffGroups.map((ffGroup) => ({key: ffGroup?.id, label: ffGroup?.name, isGroup: true}))];
    return (
      <div className={classes.mainDiv} data-testid={'booking-filters'}>
        <div>
          <div className={classes.childDiv}>
            <span className={classes.leftElement}>Origin</span>
            <Input.Group compact style={{ minWidth: '35%', maxWidth: '60%' }}>
              <Select
                data-testid={'polSearch'}
                value={polSearch}
                style={{ width: '100px' }}
                onChange={(type) => bookingSetFilter({ polSearch: type })}
              >
                {getPolPodTypesInquiryFilter.map(({key, label}) => {
                  if (key !== 'city') {
                    return <Select.Option value={key} key={key} data-testid={`pol${label}`}>{label}</Select.Option>
                  } else if (type === 'SHIPPER') {
                    return <Select.Option value={key} key={key} data-testid={`pol${label}`}>{label}</Select.Option>
                  } return null;
                })}
              </Select>
              <SearchTagsInput
                mode="multiple"
                placeholder={`Search and select a ${polSearch} `}
                style={{ minWidth: '37.73%', maxWidth: '60%' }}
                options={polDropdown}
                value={polValue}
                onChange={(value) => {
                  bookingSetFilter({
                    polList: polSearch === 'port' ? value : [],
                    originCityList: polSearch === 'city' ? value : [],
                    originCountryList: polSearch === 'country' ? value : [],
                  });
                }}
                searchFunction={(e) => this.handleSearch(polSearch, e, 'polDropdown')}
                size={'default'}
              />
            </Input.Group>
          </div>
          <div className={classes.childDiv}>
            <span className={classes.leftElement}>Destination</span>
            <Input.Group compact style={{ minWidth: '31%', maxWidth: '60%' }}>
              <Select
                data-testid={'podSearch'}
                value={podSearch}
                style={{ width: '100px' }}
                onChange={(type) => bookingSetFilter({ podSearch: type })}
              >
                {getPolPodTypesInquiryFilter.map(({key, label}) => {
                  if (key !== 'city') {
                    return <Select.Option value={key} key={key} data-testid={`pod${label}`}>{label}</Select.Option>
                  } else if (type === 'SHIPPER') {
                    return <Select.Option value={key} key={key} data-testid={`pod${label}`}>{label}</Select.Option>
                  } return null;
                })}
              </Select>
              <SearchTagsInput
                mode="multiple"
                placeholder={`Search and select a ${podSearch} `}
                style={{ minWidth: '37.73%', maxWidth: '60%' }}
                options={podDropdown}
                value={podValue}
                onChange={(value) => {
                  bookingSetFilter({
                    podList: podSearch === 'port' ? value : [],
                    destinationCityList: podSearch === 'city' ? value : [],
                    destinationCountryList: podSearch === 'country' ? value : [],
                  });
                }}
                searchFunction={(e) => this.handleSearch(podSearch, e, 'podDropdown')}
                size={'default'}
              />
            </Input.Group>
          </div>
          {type === 'SHIPPER' ? (
            <div className={classes.childDiv}>
              <span className={classes.leftElement}>Freight Forwarder</span>
              <Select
                style={{ minWidth: '30%', maxWidth: '60%' }}
                placeholder="Select FFs"
                mode="multiple"
                value={[...(this.props.filters?.ffList || []), ...(this.props.filters?.ffGroupIdList || [])]}
                onChange={(selectedItems) => {
                  const selectedFF = selectedItems.filter((item) => ffList.find((ff) => ff.key === item));
                  const selectedFFGroups = selectedItems.filter((item) => ffGroups.find((ff) => ff.id === item));
                  this.props.bookingSetFilter({
                    ffList: selectedFF,
                    ffGroupIdList: selectedFFGroups,
                  });
                }}
                data-testid={'ff-select'}
              >
                {FFsAndGroupsList?.map((item) => (
                  <Select.Option key={item.key} value={item.key} data-value={item.label}>
                    <div style={{display:'flex', justifyContent: 'space-between'}}>
                      <span>{item.label}</span>
                      <span className={item.isGroup ? classes.groupType : classes.ffType}>{item.isGroup ? 'Group' : 'FF'}</span>
                      </div>
                  </Select.Option>
                ))}
              </Select>
            </div>
          ) : null}
          {type === 'FF' ? (
            <div className={classes.childDiv}>
              <span className={classes.leftElement}>Shipper</span>
              <Select
                style={{ minWidth: '30%', maxWidth: '60%' }}
                placeholder="Select Shippers"
                mode="multiple"
                value={this.props.filters.shipperList}
                onChange={(selectedItems) => {
                  this.props.bookingSetFilter({
                    shipperList: selectedItems,
                  });
                }}
              >
                {shipperList.map((item) => (
                  <Select.Option key={item.key} value={item.key}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
          ) : null}
          {/*
        <div className={classes.childDiv}>
            <span className={classes.leftElement}>Mode</span>
            <Checkbox.Group options={modes}
              value={this.props.filters.modeList}
              onChange={(checkedValues) => {
                this.props.bookingSetFilter({
                  modeList: checkedValues,
                });
              }} />
          </div>
            */}
        </div>
        <div className={classes.childDiv}>
          <span className={classes.leftElement}>Booking Access</span>
          <SearchTagsInput
            mode="multiple"
            placeholder="Booking Access"
            style={{ minWidth: '30%', maxWidth: '60%' }}
            dropdownStyle={{ maxWidth: '240px' }}
            options={this.state.accessTypeDropdownOptions}
            value={
              filters.accessTypeFilters &&
              filters.accessTypeFilters.map((item) => {
                return !['PUBLIC', 'PRIVATE'].includes(item.accessType)
                  ? {
                    key: item.teamId,
                    label: item.teamName,
                  }
                  : {
                    key: item.accessType,
                    label: item.teamName,
                  };
              })
            }
            onChange={(access) => {
              this.props.bookingSetFilter({
                accessTypeFilters: access.map((item) => {
                  return !['PUBLIC', 'PRIVATE'].includes(item.key)
                    ? {
                      accessType: 'TEAM',
                      teamId: item.key,
                      teamName: item.label,
                    }
                    : {
                      accessType: item.key,
                      teamName: item.label,
                    };
                }),
              });
            }}
            searchFunction={() => this.searchInquiryAccess()}
            size={'default'}
          />
        </div>

        <div style={{ display: 'flex', marginLeft: '174px' }}>
          <Button
            disabled={this.props.loading}
            style={{
              width: 120,
              margin: '10px',
              backgroundColor: '#2A9FD8',
              border: '1px solid #2A9FD8',
            }}
            onClick={() => this.props.handleApply()}
            type="primary"
            data-testid={'apply-filters'}
          >
            Apply
          </Button>
          <Button style={{ width: 120, margin: '10px' }} onClick={() => this.props.handleCancel()}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ masterData, bookingInquiryReducer }, ownProps) => ({
  filters: bookingInquiryReducer.selectedFilters,
  appliedFilters: bookingInquiryReducer.appliedFilters,
  ffList: getFFDimensions(masterData),
  ffGroups: masterData.dimensionData.FF_GROUPS,
  shipperList: getShipperDimensions(masterData),
});

const mapDispatchToProps = (dispatch, ownProps) =>
  bindActionCreators(
    {
      bookingSetFilter: bookingSetFilter(ownProps.type),
    },
    dispatch
  );

const BookingFilterPopupStyled = injectSheet(styles)(BookingFilterPopup);
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(BookingFilterPopupStyled));
